<template>
	<app-header-teleport>
		<div class="flex flex-1 items-center">
			<app-title :title="$t('dashboard')" />
			<div class="ml-2">
				<app-advanced-filter @onFilter="onFilter" @onClear="onClear" :loading="isLoading">
					<div class="app-input-container">
						<label class="mb-1 text-13 text-secondary">{{ $t('type') }}:</label>

						<div class="flex flex-1 flex-row flex-wrap">
							<div
								class="cursor-pointer border-2 mr-2 mt-2 p-1"
								:class="{
									'text-primary': filters.reportTypeId === type.value,
									'border-primary': filters.reportTypeId === type.value,
									'border-gray-200': filters.reportTypeId !== type.value,
								}"
								v-for="type in reportTypeIds"
								:key="type.value"
								@click="onReportTypeSelected(type.value)"
							>
								{{ $t(type.label) }}
							</div>
						</div>
					</div>
					<app-advanced-date-filter
						v-model="filters"
						:start-date="'startDate'"
						:end-date="'endDate'"
						:with-offset="false"
					/>
				</app-advanced-filter>
			</div>
		</div>
	</app-header-teleport>
	<div class="flex flex-1">
		<app-card
			v-for="(filter, index) in statistics"
			:key="index"
			class="border-1 border-transparent cursor-pointer"
			:class="{
				'mt-4': index === 0,
			}"
			:icon="filter.icon"
			:title="filter.type"
			:text="filter.value"
			:propValue="filter.percentageRate"
			:comparison="filter.percentageRate"
		/>
	</div>
	<div class="app-input-container">
		<div class="flex flex-1 flex-row flex-wrap">
			<div
				class="cursor-pointer border-2 mr-2 mt-2 p-1"
				:class="{
					'text-primary': filters.reportTypeId === type.value,
					'border-primary': filters.reportTypeId === type.value,
					'border-gray-400': filters.reportTypeId !== type.value,
				}"
				v-for="type in reportTypeIds"
				:key="type.value"
				@click="onReportTypeSelected(type.value, true)"
			>
				{{ $t(type.label) }}
			</div>
		</div>
	</div>
	<app-section class="tight mt-4" style="height: 300px;" v-for="(chart, index) in charts" :key="index">
		<app-bar-chart name="Dashboard" :labels="chart.labels" :series="chart.series" :stacked="true" />
	</app-section>
	<app-section class="tight mt-4" style="height: 300px;">
		<app-pie-chart :labels="pieChart.labels" :series="pieChart.series" height="300" />
	</app-section>
</template>

<script>
	import { mapGetters } from 'vuex';
	import DashboardService from '@/services/DashboardService';
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter';
	import AppAdvancedDateFilter from '@/components/app/AppAdvancedDateFilter';
	import constants from '@/constants/finance';
	import moment from 'moment';
	import AppBarChart from '@/components/app/AppBarChart';
	import AppPieChart from '@/components/app/AppPieChart';
	import AppCard from '@/components/app/AppCard';

	const ORDERS_BY_YEAR = 5;
	const ORDERS_BY_MONTH = 4;
	const ORDERS_WEEKLY = 3;
	const ORDERS_HOURLY = 2;
	const ORDERS_DAILY = 1;

	const defaultFilters = {
		reportTypeId: ORDERS_HOURLY,
		startDate: moment()
			.add(-30, 'day')
			.format('YYYY-MM-DD'),
		endDate: moment().format('YYYY-MM-DD'),
	};

	export default {
		name: 'Dashboard',
		components: {
			AppAdvancedFilter,
			AppAdvancedDateFilter,
			AppBarChart,
			AppPieChart,
			AppCard,
		},
		computed: {
			...mapGetters({
				selectedRestaurants: 'selectedRestaurants',
			}),
		},
		data: () => ({
			statistics: [],
			reportTypeIds: [
				{
					value: ORDERS_BY_YEAR,
					label: 'yearly',
					chartLabelKey: 'code',
					charts: [
						[
							{ name: 'Income delivered', color: '#0eb746', prop: 'incomeDelivered', positive: true },
							{ name: 'Income rejected', color: '#cc1919', prop: 'incomeRejected', positive: false },
						],
						[
							{ name: 'Orders delivered', color: '#0eb746', prop: 'ordersNoDelivered', positive: true },
							{ name: 'Orders rejected', color: '#cc1919', prop: 'ordersNoRejected', positive: false },
						],
					],
				},
				{
					value: ORDERS_BY_MONTH,
					label: 'monthly',
					chartLabelKey: 'code',
					charts: [
						[
							{ name: 'Income delivered', color: '#0eb746', prop: 'incomeDelivered', positive: true },
							{ name: 'Income rejected', color: '#cc1919', prop: 'incomeRejected', positive: false },
						],
						[
							{ name: 'Orders delivered', color: '#0eb746', prop: 'ordersNoDelivered', positive: true },
							{ name: 'Orders rejected', color: '#cc1919', prop: 'ordersNoRejected', positive: false },
						],
					],
				},
				{
					value: ORDERS_WEEKLY,
					label: 'weekly',
					chartLabelKey: 'code',
					charts: [
						[
							{ name: 'Income delivered', color: '#0eb746', prop: 'incomeDelivered', positive: true },
							{ name: 'Income rejected', color: '#cc1919', prop: 'incomeRejected', positive: false },
						],
						[
							{ name: 'Orders delivered', color: '#0eb746', prop: 'ordersNoDelivered', positive: true },
							{ name: 'Orders rejected', color: '#cc1919', prop: 'ordersNoRejected', positive: false },
						],
					],
				},
				{
					value: ORDERS_HOURLY,
					label: 'hourly',
					chartLabelKey: 'code',
					charts: [
						[{ name: 'Income', color: '#4387e7', prop: 'income', positive: true }],
						[{ name: 'Orders', color: '#0eb746', prop: 'ordersNo', positive: true }],
					],
				},
				{
					value: ORDERS_DAILY,
					label: 'daily',
					chartLabelKey: 'code',
					charts: [
						[{ name: 'Income delivered', color: '#0eb746', prop: 'income', positive: true }],
						[{ name: 'Orders delivered', color: '#0eb746', prop: 'ordersNo', positive: true }],
					],
				},
			],
			filters: { ...defaultFilters },
			searchFilters: { ...defaultFilters },
			charts: [],
			pieChart: {
				labels: [],
				series: [],
			},
			isLoading: true,
		}),
		methods: {
			init() {
				this.getData();
				this.getStatistics();
			},
			getData() {
				this.isLoading = true;
				DashboardService.get({
					...this.searchFilters,
					businessIDs: this.selectedRestaurants.join('-'),
				}).then((data) => {
					const reportType = this.reportTypeIds.find((r) => r.value === this.filters.reportTypeId);

					this.charts = reportType.charts.map((chart) => {
						return {
							labels: data.map((i) => i[reportType.chartLabelKey]),
							series: chart.map((bar) => {
								const seriesData = data.map(
									(i) => (bar.positive ? 1 : -1) * Math.floor(parseInt(i[bar.prop]))
								);
								return {
									name: bar.name,
									color: bar.color,
									data: seriesData,
								};
							}),
						};
					});
					this.isLoading = false;
				});
			},
			getStatistics() {
				DashboardService.statistics({
					...this.searchFilters,
					businessIDs: this.selectedRestaurants.join('-'),
				}).then(({ chart, statistics }) => {
					this.statistics = statistics;
					this.pieChart = {
						labels: chart.map(({ orderType }) => orderType),
						series: chart.map(({ ordersNo }) => ordersNo),
					};
				});
			},
			onFilter() {
				this.searchFilters = { ...this.filters };
				this.init();
			},
			onClear() {
				this.filters = { ...constants.defaultFilters };
				this.searchFilters = { ...constants.defaultFilters };
				this.init();
			},
			onReportTypeSelected(type, search) {
				this.filters.reportTypeId = type;
				if (search) {
					this.onFilter();
				}
			},
		},
		mounted() {
			this.init();
		},
		watch: {
			selectedRestaurants() {
				if (!this.$store.getters.token) return;
				this.init();
			},
		},
	};
</script>
